import { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAtomValue } from 'jotai';
import imageCompression from 'browser-image-compression';
import '../../common/Mypage.scss';
import '../../../recruit/common/Recruit.scss';
import './ProfilePageEdit.scss';
import MainURL from '../../../../MainURL';
import Loading from '../../../../components/Loading';
import { userDataAtom } from '../../../../AtomStore';
import MypageMenu from '../../common/MypageMenu';
import { buildTimeline, profileMainImageSrc } from '../../../minister/pages/ministerPage/ministerPageUtils';
import type { MinisterProfile, TimelineItem } from '../../../minister/pages/ministerPage/ministerPageUtils';

interface MyProfileResponse {
  hasCommon: boolean;
  hasProfile: boolean;
  latestResumeId: string;
  resumeCount: number;
  profile: MinisterProfile | null;
}

// 연혁 편집용 — 한 해의 내용은 줄바꿈으로 구분
interface TimelineEditItem {
  year: string;
  text: string;
}

const toEditTimeline = (list: TimelineItem[]): TimelineEditItem[] =>
  list.map((item) => ({ year: item.year, text: item.content.join('\n') }));

const fromEditTimeline = (list: TimelineEditItem[]): TimelineItem[] =>
  list
    .map((item) => ({
      year: item.year.trim(),
      content: item.text.split('\n').map((line) => line.trim()).filter(Boolean),
    }))
    .filter((item) => item.year || item.content.length > 0);

// 마이페이지 > 프로필페이지 관리
// 공통 데이터(이름·연락처·사진·교회·교단·직분·학력·경력·포트폴리오)는 이력서에서 수정하고,
// 여기서는 프로필페이지 전용 데이터만 저장한다
export default function ProfilePageEdit() {
  const navigate = useNavigate();
  const userData = useAtomValue(userDataAtom);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [info, setInfo] = useState<MyProfileResponse | null>(null);

  const [isPublic, setIsPublic] = useState(false);
  const [sort, setSort] = useState('');
  const [subNameSort, setSubNameSort] = useState('직분');
  const [ministryFieldsText, setMinistryFieldsText] = useState('');
  const [showPhone, setShowPhone] = useState(false);
  const [showEmail, setShowEmail] = useState(false);
  const [useCustomTimeline, setUseCustomTimeline] = useState(false);
  const [timeline, setTimeline] = useState<TimelineEditItem[]>([]);
  const [mainImage, setMainImage] = useState('');
  const [mainImageFile, setMainImageFile] = useState<File | null>(null);
  const [mainImagePreview, setMainImagePreview] = useState('');

  const load = () => {
    if (!userData.userAccount) {
      setLoading(false);
      return;
    }
    setLoading(true);
    axios.get(`${MainURL}/ministerprofile/my`, { params: { userAccount: userData.userAccount } })
      .then((res) => {
        if (!res.data?.success) return;
        const data = res.data as MyProfileResponse;
        setInfo(data);
        const profile = data.profile;
        if (!profile) return;
        setIsPublic(profile.isPublic);
        setSort(profile.sort);
        setSubNameSort(profile.subNameSort || '직분');
        setMinistryFieldsText(profile.ministryFields.join(', '));
        setShowPhone(profile.showPhone);
        setShowEmail(profile.showEmail);
        setUseCustomTimeline(profile.timeline.length > 0);
        setTimeline(toEditTimeline(profile.timeline));
        setMainImage(profile.mainImage);
        setMainImageFile(null);
        setMainImagePreview('');
      })
      .catch((error) => {
        console.error('프로필페이지 정보 조회 실패:', error);
        alert('프로필페이지 정보를 불러오지 못했습니다.');
      })
      .finally(() => setLoading(false));
  };

  useEffect(load, [userData.userAccount]);

  const profile = info?.profile || null;

  const goResume = () => {
    navigate(info?.latestResumeId ? `/mypage/resumeedit?id=${info.latestResumeId}` : '/mypage/resumeedit');
    window.scrollTo(0, 0);
  };

  const handleMainImage = async (file?: File) => {
    if (!file) return;
    const compressed = await imageCompression(file, { maxSizeMB: 1, maxWidthOrHeight: 1600 });
    const named = new File([compressed], file.name, { type: file.type });
    setMainImageFile(named);
    setMainImagePreview(URL.createObjectURL(named));
  };

  const removeMainImage = () => {
    setMainImage('');
    setMainImageFile(null);
    setMainImagePreview('');
  };

  // 직접 입력을 처음 켤 때 — 학력·경력으로 만든 연혁을 시작점으로
  const toggleCustomTimeline = (checked: boolean) => {
    setUseCustomTimeline(checked);
    if (checked && timeline.length === 0 && profile) {
      const auto = buildTimeline({ ...profile, timeline: [] });
      setTimeline(auto.length ? toEditTimeline(auto) : [{ year: '', text: '' }]);
    }
  };

  const handleSave = async () => {
    if (saving) return;
    setSaving(true);
    const formData = new FormData();
    formData.append('userAccount', userData.userAccount);
    formData.append('isPublic', isPublic ? 'true' : 'false');
    formData.append('sort', sort);
    formData.append('subNameSort', subNameSort);
    formData.append('ministryFields', JSON.stringify(ministryFieldsText.split(',').map((v) => v.trim()).filter(Boolean)));
    formData.append('timeline', JSON.stringify(useCustomTimeline ? fromEditTimeline(timeline) : []));
    formData.append('showPhone', showPhone ? 'true' : 'false');
    formData.append('showEmail', showEmail ? 'true' : 'false');
    if (mainImageFile) formData.append('mainImage', mainImageFile);
    if (!mainImage && !mainImageFile) formData.append('removeMainImage', 'true');

    try {
      const res = await axios.post(`${MainURL}/ministerprofile/save`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (res.data?.success) {
        alert('프로필페이지가 저장되었습니다.');
        load();
      } else {
        alert(res.data?.error || '저장에 실패했습니다.');
      }
    } catch (error: any) {
      console.error('프로필페이지 저장 실패:', error);
      alert(error?.response?.data?.error || '저장에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  };

  const previewSrc = mainImagePreview || (mainImage && profile ? profileMainImageSrc({ mainImage, profileImage: '' }) : '');
  const photoCount = profile?.portfolioItems.filter((item) => item.category === '사역사진').length ?? 0;
  const videoCount = (profile?.portfolioItems.length ?? 0) - photoCount;

  return (
    <div className="mypage" style={{ paddingBottom: '100px' }}>
      <div className="inner">
        <MypageMenu />
        <div className="subpage__main">
          <div className="mypage-panel mypage-section-card profile-page-edit">
            <div className="main_title_row">
              <h3 className="main_title">프로필페이지 관리</h3>
              <div className="postBtnbox">
                {info?.hasProfile && profile?.id && (
                  <div
                    className="postBtn"
                    style={{ marginRight: '10px' }}
                    onClick={() => {
                      navigate(`/minister/profilepage/detail?id=${profile.id}`);
                      window.scrollTo(0, 0);
                    }}
                  >
                    <p>내 프로필페이지 보기</p>
                  </div>
                )}
              </div>
            </div>

            {loading ? (
              <div style={{ display: 'flex', justifyContent: 'center', padding: '50px' }}>
                <Loading />
              </div>
            ) : !info?.hasCommon || !profile ? (
              <div className="profile-page-edit__empty">
                <p>프로필페이지의 이름·연락처·학력·경력·포트폴리오는 이력서와 함께 관리합니다.</p>
                <p>이력서를 먼저 작성하면 프로필페이지를 만들 수 있어요.</p>
                <button type="button" className="profile-page-edit__btn is-primary" onClick={goResume}>
                  이력서 작성하기
                </button>
              </div>
            ) : (
              <>
                {/* 공통 정보 — 이력서에서 수정 */}
                <section className="profile-page-edit__section">
                  <div className="profile-page-edit__section-head">
                    <h1 className="section_title">공통 정보</h1>
                    <button type="button" className="profile-page-edit__btn" onClick={goResume}>
                      {info.resumeCount > 0 ? '이력서에서 수정' : '이력서 작성하기'}
                    </button>
                  </div>
                  <p className="profile-page-edit__hint">
                    아래 항목은 모든 이력서와 프로필페이지에 함께 쓰입니다. 이력서에서 고치면 프로필페이지에도 바로 반영됩니다.
                  </p>
                  <dl className="profile-page-edit__common">
                    <div><dt>이름</dt><dd>{profile.name || '-'}</dd></div>
                    <div><dt>현재 교회</dt><dd>{profile.church || '-'}</dd></div>
                    <div><dt>교단</dt><dd>{profile.religiousbody || '-'}</dd></div>
                    <div><dt>직분</dt><dd>{profile.dutyTitle || '-'}</dd></div>
                    <div><dt>휴대폰</dt><dd>{profile.phone || '-'}</dd></div>
                    <div><dt>이메일</dt><dd>{profile.email || '-'}</dd></div>
                    <div><dt>학력 · 경력</dt><dd>학력 {profile.educationItems.length}건 · 경력 {profile.careerItems.length}건</dd></div>
                    <div><dt>포트폴리오</dt><dd>사진 {photoCount}장 · 영상 {videoCount}개</dd></div>
                  </dl>
                </section>

                {/* 프로필페이지 전용 */}
                <section className="profile-page-edit__section">
                  <h1 className="section_title">프로필페이지 설정</h1>
                  <div className="inputCover">
                    <div className="inputBox">
                      <h3 className="title"><p>공개</p></h3>
                      <div className="inputRow">
                        <label className="profile-page-edit__check mm-check">
                          <input className="checkbox" type="checkbox" checked={isPublic} onChange={(e) => setIsPublic(e.target.checked)} />
                          <span className="checkmark" />
                          사역자정보 &gt; 프로필페이지 목록에 공개합니다
                        </label>
                      </div>
                    </div>

                    <div className="inputBox">
                      <h3 className="title"><p>대표사진</p></h3>
                      <div className="inputRow">
                        <p className="profile-page-edit__hint">상단을 가득 채우는 가로 사진입니다. 없으면 증명사진을 씁니다.</p>
                        {previewSrc && <img className="profile-page-edit__main-img" src={previewSrc} alt="대표사진" />}
                        <div className="profile-page-edit__row">
                          <label className="profile-page-edit__btn">
                            {previewSrc ? '사진 바꾸기' : '사진 올리기'}
                            <input
                              type="file"
                              accept="image/*"
                              hidden
                              onChange={(e) => {
                                handleMainImage(e.target.files?.[0]);
                                e.target.value = '';
                              }}
                            />
                          </label>
                          {previewSrc && (
                            <button type="button" className="profile-page-edit__btn" onClick={removeMainImage}>삭제</button>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="inputCoverTwoColumns">
                      <div className="inputBox">
                        <h3 className="title"><p>구분</p></h3>
                        <div className="inputRow">
                          <input className="inputdefault" type="text" value={sort} placeholder="예: 담임목사, 찬양사역자"
                            onChange={(e) => setSort(e.target.value)} />
                        </div>
                      </div>
                      <div className="inputBox">
                        <h3 className="title"><p>이름 아래 표시</p></h3>
                        <div className="inputRow">
                          <div className="profile-page-edit__row">
                            {['직분', '구분'].map((option) => (
                              <label key={option} className="profile-page-edit__check">
                                <input type="radio" name="subNameSort" checked={subNameSort === option}
                                  onChange={() => setSubNameSort(option)} />
                                {option}
                                <span className="profile-page-edit__muted">
                                  ({(option === '직분' ? profile.dutyTitle : sort) || '비어 있음'})
                                </span>
                              </label>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="inputBox">
                      <h3 className="title"><p>담당 사역</p></h3>
                      <div className="inputRow">
                        <input className="inputdefault" type="text" value={ministryFieldsText}
                          placeholder="쉼표로 구분 — 예: 청년부, 찬양, 새가족"
                          onChange={(e) => setMinistryFieldsText(e.target.value)} />
                      </div>
                    </div>

                    <div className="inputBox">
                      <h3 className="title"><p>연락처 공개</p></h3>
                      <div className="inputRow">
                        <div className="profile-page-edit__row">
                          <label className="profile-page-edit__check mm-check">
                            <input className="checkbox" type="checkbox" checked={showPhone} disabled={!profile.phone}
                              onChange={(e) => setShowPhone(e.target.checked)} />
                            <span className="checkmark" />
                            휴대폰 <span className="profile-page-edit__muted">{profile.phone || '(이력서에 없음)'}</span>
                          </label>
                          <label className="profile-page-edit__check mm-check">
                            <input className="checkbox" type="checkbox" checked={showEmail} disabled={!profile.email}
                              onChange={(e) => setShowEmail(e.target.checked)} />
                            <span className="checkmark" />
                            이메일 <span className="profile-page-edit__muted">{profile.email || '(이력서에 없음)'}</span>
                          </label>
                        </div>
                        <p className="profile-page-edit__hint">체크한 연락처만 프로필페이지에 보입니다.</p>
                      </div>
                    </div>

                    <div className="inputBox">
                      <h3 className="title"><p>연혁</p></h3>
                      <div className="inputRow">
                        <label className="profile-page-edit__check mm-check">
                          <input className="checkbox" type="checkbox" checked={useCustomTimeline}
                            onChange={(e) => toggleCustomTimeline(e.target.checked)} />
                          <span className="checkmark" />
                          직접 입력하기
                        </label>
                        {!useCustomTimeline ? (
                          <p className="profile-page-edit__hint">이력서의 학력·경력으로 연혁을 자동으로 만듭니다.</p>
                        ) : (
                          <div className="profile-page-edit__timeline">
                            {timeline.map((item, index) => (
                              <div key={index} className="profile-page-edit__timeline-item">
                                <input className="inputdefault" type="text" value={item.year} placeholder="연도"
                                  onChange={(e) => setTimeline(timeline.map((t, i) => (i === index ? { ...t, year: e.target.value } : t)))} />
                                <textarea className="inputdefault" value={item.text} rows={3} placeholder="한 줄에 하나씩"
                                  onChange={(e) => setTimeline(timeline.map((t, i) => (i === index ? { ...t, text: e.target.value } : t)))} />
                                <button type="button" className="profile-page-edit__btn"
                                  onClick={() => setTimeline(timeline.filter((_, i) => i !== index))}>
                                  삭제
                                </button>
                              </div>
                            ))}
                            <button type="button" className="profile-page-edit__btn"
                              onClick={() => setTimeline([...timeline, { year: '', text: '' }])}>
                              연도 추가
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </section>

                <div className="profile-page-edit__save">
                  <button type="button" className="profile-page-edit__btn is-primary" onClick={handleSave} disabled={saving}>
                    {saving ? '저장 중...' : '저장하기'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
